import { ColumnSort } from "@tanstack/react-table";
import {
  getControlCategory,
  getSCMControls,
  useDebounceState,
} from "@theprelude/core";
import {
  ControlCategories,
  PartnerInboxes,
  PartnerInboxQueryKey,
  SCMCategories,
  ValidControlCode,
} from "@theprelude/sdk";
import { useMemo, useState } from "react";
import { mergeDeep } from "remeda";
import { useControlFilters } from "@/components/filter-form/available-filters";
import { getOdataFilters } from "@/components/filter-form/odata";
import { ResponsiveContainer } from "@/components/responsive-container";
import {
  getColumnVisibility,
  getExpectedRowClassName,
  sortByField,
} from "@/components/table/columns";
import { SCMTableFilterInput } from "@/components/table/filter-input";
import { TableFilterSelector } from "@/components/table/filter-selector";
import { useTable } from "@/components/table/hooks";
import { InfiniteTable } from "@/components/table/infinite-table";
import { useInfiniteInboxes } from "@/hooks/use-infinite-inboxes";
import { Filter } from "@/lib/filter";
import {
  useExceptedFilters,
  useTopLevelFiltersFor,
} from "@/queries/control-monitor";
import { AddObjectExceptionButton } from "../add-object-exception-button";
import { ExceptedFilterToggle } from "../excepted-filter-toggle";
import { ExportInboxesIconButton } from "../export-inboxes-button";
import type { ODataObjectFilter } from "../overlays/schema";
import { TopLevelFilterIndicator } from "../top-level-filter-indicator";
import { ResultCount } from "./result-count";
import {
  formatSCMInboxesData,
  SCMInboxEntry,
  useEmailControls,
  useSCMInboxesColumns,
} from "./scm-columns";
import { SCMEmptyState } from "./scm-table-empty-state";

const searchField = (field: PartnerInboxQueryKey, value: string) => ({
  [field]: { contains: value },
});

const getRowId = (endpoint: SCMInboxEntry) => endpoint.email;

export const InboxesTable = ({
  query: baseQuery,
  control,
  instanceId,
  filters,
  onFilter,
}: {
  query: ODataObjectFilter<PartnerInboxes>;
  control?: ValidControlCode;
  instanceId?: string;
  filters: Filter[];
  onFilter: (filters: Filter[]) => void;
}) => {
  const [columnSort, setColumnSort] = useState<ColumnSort[]>([]);
  const searchFilter = useDebounceState("");
  const searchValue = searchFilter.debouncedValue.toLowerCase();

  const topLevelFilters = useTopLevelFiltersFor("inboxes");
  const exceptedFilters = useExceptedFilters(ControlCategories.EMAIL);

  const emailProviders = useEmailControls();

  const columns = useSCMInboxesColumns();

  const query = useMemo(() => {
    const sortedColumn = columns.find((c) => c.id === columnSort[0]?.id);
    return {
      filter: {
        and: [
          control !== undefined && instanceId !== undefined
            ? mergeDeep(baseQuery, {
                instances: { any: { control, instance_id: instanceId } },
              })
            : baseQuery,
          ...getOdataFilters(filters),
          ...getOdataFilters(topLevelFilters),
          ...getOdataFilters(exceptedFilters),
          searchValue && {
            or: [
              searchField("email", searchValue),
              {
                instances: {
                  any: {
                    or: [
                      searchField("inbox_type", searchValue),
                      searchField("domain", searchValue),
                      searchField("principal_name", searchValue),
                    ],
                  },
                },
              },
            ],
          },
        ],
      },
      orderBy: sortByField(columnSort[0], sortedColumn?.meta?.odata),
    };
  }, [
    baseQuery,
    columns,
    columnSort,
    control,
    exceptedFilters,
    filters,
    instanceId,
    searchValue,
    topLevelFilters,
  ]);

  const { data, fetchNextPage, hasNextPage, isFetching, isLoading } =
    useInfiniteInboxes({ query });

  const inboxes = useMemo(
    () => formatSCMInboxesData({ inboxes: data?.data ?? [], emailProviders }),
    [data, emailProviders],
  );

  const fetchedCount = inboxes.length;
  const totalCount = data?.totalCount ?? 0;

  const availableFilters = useControlFilters(
    control ? [control] : getSCMControls(SCMCategories.INBOX),
  );

  const table = useTable({
    name: `inboxes-${JSON.stringify(baseQuery)}`,
    data: inboxes,
    columns,
    manualSorting: true,
    manualFiltering: true,
    initialState: {
      columnVisibility: getColumnVisibility(columns),
    },
    state: {
      sorting: columnSort,
      globalFilter: searchValue,
    },
    onSortingChange: setColumnSort,
    getRowId,
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center gap-2">
        <SCMTableFilterInput
          value={searchFilter.value}
          onChange={(value) => searchFilter.setDelayed(value)}
          onClear={() => searchFilter.setImmediate("")}
        />
        <TableFilterSelector
          availableFilters={availableFilters}
          currentFilters={filters}
          setFilters={onFilter}
          category={ControlCategories.EMAIL}
        />
        <ExportInboxesIconButton query={query} />
      </div>
      <TopLevelFilterIndicator category="inboxes" />
      <div className="flex items-center justify-between">
        <ResultCount
          loading={isLoading}
          count={fetchedCount}
          totalCount={totalCount}
          resourceType="inbox"
        />
        <ExceptedFilterToggle />
        {control && (
          <AddObjectExceptionButton category={getControlCategory(control)} />
        )}
      </div>
      <ResponsiveContainer className="flex-1 overflow-hidden">
        {({ height }) => (
          <InfiniteTable
            table={table}
            loading={isLoading}
            isFetching={isFetching}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            height={height}
            emptyState={<SCMEmptyState resourceType="inbox" />}
            rowClassName={getExpectedRowClassName}
          />
        )}
      </ResponsiveContainer>
      <div className="h-2" />
    </div>
  );
};
